import type { QuoteDocument, QuoteTaxRate } from 'paintscout'
import { parseNumber } from '../util/parse-number'
import { roundPrice } from '../util/round-price'

/**
 * The rounded amount of one tax line. From quote version 14, `tax()` sums
 * these, so the quote's total tax always equals the sum of the lines that
 * `convertTaxes()` displays.
 * see https://github.com/PaintScout/PaintScout/issues/24711
 */
export function taxLineAmount(quote: QuoteDocument, detail: QuoteTaxRate): number {
  const price = parseNumber(quote.totals.after_discount)
  const materialRate = parseNumber(detail.materialRate)
  const laborRate = parseNumber(detail.laborRate)

  if (materialRate || laborRate) {
    const materials = parseNumber(quote.totals.materials)
    const labor = price - materials
    return roundPrice(materials * materialRate * 0.01 + labor * laborRate * 0.01)
  }

  return roundPrice(price * parseNumber(detail.rate) * 0.01)
}

/**
 * The sum of the rounded tax lines, skipping `Notax`. Returns undefined below
 * quote version 14, or when the quote has no tax lines, so callers can fall
 * back to the summed `tax_rate`.
 */
export function taxLinesTotal(quote: QuoteDocument): number | undefined {
  const taxLines = (quote.tax_details ?? []).filter((detail) => detail.key !== 'Notax')
  if (!(quote.version >= 14) || !taxLines.length) {
    return undefined
  }
  return roundPrice(taxLines.reduce((total, detail) => total + taxLineAmount(quote, detail), 0))
}
